import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { GridRow } from 'dibs-elements/exports/Grid';
import HpRespPromoModulePromoItem from './HpRespPromoModulePromoItem';
import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { trackPromoImpressions, trackPromoClick } from './helpers/PromoTracking';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';

import { HpRespPromoModuleSecondary_componentModule } from './__generated__/HpRespPromoModuleSecondary_componentModule.graphql';

type Props = {
    componentModule: HpRespPromoModuleSecondary_componentModule;
    promoPrimaryItemsCount: number;
    useLazyLoadImages: boolean;
    isMobile: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpRespPromoModuleSecondary: FC<Props> = ({
    componentModule,
    promoPrimaryItemsCount,
    useLazyLoadImages,
    isMobile,
    moduleIndex,
    totalModules,
}) => {
    if (!componentModule) {
        return null;
    }

    const { messages } = componentModule;
    const { title } = messages || {};
    const items = (messages?.items || []).filter(filterNulls);
    const isSinglePromo = items.length === 1;

    const handleInitialDisplay = ({ inViewport }: { inViewport: boolean }): void => {
        if (componentModule && inViewport) {
            trackPromoImpressions({
                items,
                startingIndex: promoPrimaryItemsCount,
            });
        }
    };

    return (
        <HpRespModuleContainer addTopGap={!title}>
            <HpRespModuleContainer.Header title={title} />
            <SharedInViewport stopWhenInViewport onInViewportChange={handleInitialDisplay}>
                <GridRow>
                    {items.map((promotion, index) => (
                        <HpRespPromoModulePromoItem
                            key={promotion?.cmsId || index}
                            promotion={promotion}
                            useLazyLoadImages={useLazyLoadImages}
                            onClick={() => {
                                const promoIndex = index + promoPrimaryItemsCount;
                                trackPromoClick({
                                    item: promotion,
                                    index: promoIndex,
                                });
                                trackModuleLocation({
                                    label: `promo banner #${promoIndex}`,
                                    moduleIndex,
                                    totalModules,
                                });
                            }}
                            isSinglePromo={isSinglePromo}
                            isMobile={isMobile}
                        />
                    ))}
                </GridRow>
            </SharedInViewport>
        </HpRespModuleContainer>
    );
};

export default createFragmentContainer(HpRespPromoModuleSecondary, {
    componentModule: graphql`
        fragment HpRespPromoModuleSecondary_componentModule on PromoModule {
            messages(userType: $userType) {
                title
                items {
                    cmsId
                    linkData {
                        # path is used in PromoTrackingOld.js
                        path
                    }
                    ...HpRespPromoModulePromoItem_promotion
                }
            }
        }
    `,
});
